import store from '@/store';
import log from '@/configured/logger';
import {
  createBootstrapContext,
  normalizeBootstrapOptions,
  runBootstrapLoadWithOptions,
} from './shared-load';

/**
 * Safely dispatch a store action, catching and logging any errors
 * so one failing data source doesn't break the whole app.
 */
async function safeDispatch(action, forceInline = false) {
  try {
    await store.dispatch(action, forceInline);
  } catch (error) {
    log.warn(`Non-critical error loading ${action}:`, error);
    // Don't re-throw - allow app to continue with partial data
  }
}

export async function loadBaseDataIfNeeded(payload = false) {
  const { forceInline, mode, bootstrapContext: existingContext } =
    normalizeBootstrapOptions(payload);
  const bootstrapContext =
    existingContext || createBootstrapContext({ mode });

  return runBootstrapLoadWithOptions(
    bootstrapContext,
    async () => {
      log.info('Loading base data', bootstrapContext);

      const dispatchPayload = {
        forceInline,
        mode,
        bootstrapContext,
      };

      // Don't import all of these namespaced stores to avoid early startup issues
      // Use Promise.allSettled to continue even if some fail
      const results = await Promise.allSettled([
        safeDispatch('home/loadHomeDataIfNeeded', dispatchPayload),
        safeDispatch('favorites/loadFavoritesDataIfNeeded', dispatchPayload),
        safeDispatch('navigation/loadNavigationDataIfNeeded', dispatchPayload),
        safeDispatch('products/loadProductDataIfNeeded', dispatchPayload),
      ]);

      // Log any failures but don't crash
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          const actions = ['home', 'favorites', 'navigation', 'products'];
          log.warn(`Failed to load ${actions[index]} data:`, result.reason);
        }
      });

      // These are slow - dispatch but don't wait
      safeDispatch('training/loadTrainingDataIfNeeded', dispatchPayload);
    },
    { forceReload: forceInline }
  );
}
